export type TripType =
  | 'Vision Trip'
  | 'Skilled Labor'
  | 'Volunteer'
  | 'Youth Trip'
  | 'Adult Trip'
  | 'Family Trip'
  | 'Semesters'
  | 'Gap'
  | 'WR'
  | 'Leadership'
  | 'Partner'
  | 'Other'

export type GroupStatus = 'Tentative' | 'Confirmed' | 'Cancelled'

/** New vs Return for new/return tracking */
export type GroupRetention = 'New' | 'Return'

export type BlockSeverity = 'Hard Block' | 'Soft Block'

export interface Week {
  id: string
  week_start: string
  week_end: string
  capacity: number
  notes: string | null
  created_at: string
  updated_at: string
}

export interface Group {
  id: string
  /** Region ID; API may return as `region` or `regionId`. */
  regionId?: number
  region?: number
  group_name: string
  trip_type: TripType | null
  /** Expected participants entered on the group. */
  participants: number
  /** Actual linked volunteers in group_volunteers. */
  registered_participants?: number
  /** YYYY-MM-DD or null when synced from ServiceReef without dates. */
  start_date: string | null
  end_date: string | null
  status: GroupStatus
  /** New/Return for tracking; column is `retention_group`. API may also return 'Retention' (displayed as Return). */
  retention_group?: GroupRetention | 'Retention' | null
  /** ReliefHub user ID for group contact; name/email derived from user. */
  contactUserId?: number | null
  contact_name: string | null
  contact_email: string | null
  contact_phone: string | null
  church_org: string | null
  notes: string | null
  leader_assigned: boolean
  leader_name: string | null
  leader_email?: string | null
  leader_phone?: string | null
  pretrip_info_given: boolean
  deposit_received: boolean
  background_check_complete: boolean
  full_payment_received: boolean
  post_trip_info: boolean
  dietary_restrictions: boolean
  dietary_restrictions_override?: boolean | null
  has_member_dietary_data?: boolean
  dietary_restrictions_details: string | null
  project_id: string | null
  /** ServiceReef event id when group came from sync. */
  sr_event_id?: number | null
  /** Any group_volunteer membership updated in the last 7 days (insert / manual add). */
  has_recent_member_join?: boolean
  created_at: string
  updated_at: string
}

export interface Block {
  id: string
  start_date: string
  end_date: string
  reason: string
  severity: BlockSeverity
  created_at: string
  updated_at: string
}

/** Capacity exception from API (blocks are capacity=0; overrides have capacity>0) */
export interface CapacityException {
  id: string
  start_date: string
  end_date: string
  capacity: number
  reason: string | null
  severity: 'Hard Block' | 'Soft Block' | null
  created_at?: string
  updated_at?: string
}

export interface WeekWithStats extends Week {
  filledSpots: number
  availableSpots: number
  fillRate: number
  groups: Group[]
  blocks: Block[]
  hasHardBlock: boolean
  hasSoftBlock: boolean
}

export interface Participant {
  id: string
  group_id: string
  full_name: string
  email: string | null
  phone: string | null
  organization_name?: string | null
  notes: string | null
  created_at: string
  updated_at: string
  /** Admin / Base Leader only from STM participants API. */
  price?: string | null
  paid?: string | null
}

export const TRIP_TYPES: TripType[] = [
  'Vision Trip',
  'Skilled Labor',
  'Volunteer',
  'Youth Trip',
  'Adult Trip',
  'Family Trip',
  'Semesters',
  'Gap',
  'WR',
  'Leadership',
  'Partner',
  'Other',
]

/** Select / form sentinel for `trip_type: null` (e.g. ServiceReef sync). Not a display trip type. */
export const TRIP_TYPE_UNSET = '__trip_type_unset__' as const

export const GROUP_STATUSES: GroupStatus[] = ['Tentative', 'Confirmed', 'Cancelled']

export const GROUP_RETENTION_OPTIONS: GroupRetention[] = ['New', 'Return']

export const BLOCK_SEVERITIES: BlockSeverity[] = ['Hard Block', 'Soft Block']


